import { Text } from "@wojo/ui";
import React from "react";
import {
    GcDepartureRuleLevel,
    GcDepartureRuleLineItemType,
} from "../../../../generated/graphql";
import { useTripBuilder } from "../context/TripBuilderContext";
import {
    TripBuilderQuoteContextProvider,
    useTripBuilderQuote,
} from "../context/TripBuilderQuoteStepContext";
import { QuoteFootnotes } from "./QuoteFootnotes";
import { LowDpdPriceMessage } from "./LowDpdPriceMessage";
import { QuoteAffirm } from "./QuoteAffirm";
import { QuoteAutopay } from "./QuoteAutopay";
import { SectionDepartureDates } from "../message-views/SectionDepartureDates";
import { QuoteExtensions } from "./QuoteExtensions";
import { QuoteFlightPicker } from "./QuoteFlightPicker";
import { QuoteGroupsDepartureDateAndPrice } from "./QuoteGroupsDepartureDateAndPrice";
import { QuoteGroupsIncludedExcursions } from "./QuoteGroupsIncludedExcursions";
import { QuoteGroupsRequiredFlightPicker } from "./QuoteGroupsRequiredFlightPicker";
import { QuoteMarketingBanner } from "./QuoteMarketingBanner";
import { QuoteNotice } from "./QuoteNotice";
import { QuoteSavings } from "./QuoteSavings";
import { QuoteSubmit } from "./QuoteSubmit";
import { QuoteTotalSummary } from "./QuoteTotalSummary";
import { QuoteTravelers } from "./QuoteTravelers";
import styles from "./TripBuilderQuoteStep.module.scss";
import { useMapDepartureStatus } from "../hooks/use-map-departure-status";
import { useTourPageContext } from "../../../_client/context";
import { useShowMarketingMessageView } from "../context/use-show-marketing-message-view";

export const TripBuilderQuoteStep: React.FC = () => {
    const { departure, gcDeparture } = useTripBuilder();
    const { callToBookDpd } = useTourPageContext();
    const departureStatus = useMapDepartureStatus(departure, callToBookDpd);
    const showMarketingMessageView = useShowMarketingMessageView();

    if (showMarketingMessageView || departureStatus !== "Available") {
        return null;
    }
    const classLevelMessage =
        departure?.tourContent?.tour?.classLevelContent
            ?.tripBuilderSelectedMessage;

    const flightGroupRule = gcDeparture?.lineItemRules?.find(
        (rule) =>
            rule.lineItemType === GcDepartureRuleLineItemType.DepartingFlight ||
            rule.lineItemType === GcDepartureRuleLineItemType.ReturningFlight,
    );

    const specificFlightsAreRequired =
        flightGroupRule?.level === GcDepartureRuleLevel.Required;
    const specificFlightsAreRecommended =
        flightGroupRule?.level === GcDepartureRuleLevel.Recommended;

    return (
        <TripBuilderQuoteContextProvider>
            {gcDeparture ? (
                <QuoteGroupsDepartureDateAndPrice />
            ) : (
                <SectionDepartureDatesWrapper />
            )}
            <QuoteMarketingBanner />
            <LowDpdPriceMessage />
            {classLevelMessage && (
                <Text.Body
                    emphasis="1"
                    className={styles["class-level-message"]}
                >
                    {classLevelMessage}
                </Text.Body>
            )}
            <div className={styles.configurations}>
                <QuoteTravelers />
                {specificFlightsAreRequired ? (
                    <QuoteGroupsRequiredFlightPicker />
                ) : (
                    <QuoteFlightPicker
                        autoSelectGeolocation={!specificFlightsAreRecommended}
                    />
                )}
                <QuoteExtensions />
            </div>

            <QuoteGroupsIncludedExcursions />
            <QuoteSavings />
            <QuoteTotalSummary />
            {!!process.env.NEXT_PUBLIC_AFFIRM_KEY ? (
                <QuoteAffirm />
            ) : (
                <QuoteAutopay />
            )}
            <div className={styles.submit}>
                <QuoteNotice />
                <QuoteSubmit />
            </div>
            <QuoteFootnotes />
        </TripBuilderQuoteContextProvider>
    );
};

const SectionDepartureDatesWrapper: React.FC = () => {
    const { draftQuote } = useTripBuilderQuote();
    return (
        <SectionDepartureDates
            departureDate={draftQuote?.order.trips.at(0)?.departureDate}
            endDate={draftQuote?.order.trips.at(0)?.endDate}
        />
    );
};
